import { createGlobalStyle, css } from '@withjoy/joykit';
import { STANDARD_TIMING_FUNCTION } from '@shared/utils/animationTransition';

export const baseState = css`
  transition:
    background-color 0.2s ${STANDARD_TIMING_FUNCTION},
    outline 0.2s ${STANDARD_TIMING_FUNCTION},
    color 0.2s ${STANDARD_TIMING_FUNCTION},
    opacity 0.2s ${STANDARD_TIMING_FUNCTION},
    border 0.2s ${STANDARD_TIMING_FUNCTION},
    box-shadow 0.2s ${STANDARD_TIMING_FUNCTION};
  outline: 0.125rem solid ${props => props.theme.colors.transparent};
  outline-offset: -0.125rem;
`;

export const buttonStates = css`
  ${baseState};

  &:hover {
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow:
      0 7px 27px 0 rgba(44, 41, 37, 0.06),
      0 4px 16px 0 rgba(0, 0, 0, 0.07);
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.colors.newUIBrandPrimary};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.mono3};
    outline-color: ${props => props.theme.colors.transparent};
  }
`;

export const filledButtonStates = css`
  ${baseState};

  &:hover {
    background-color: ${({ theme }) => theme.colors.mono3};
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.colors.newUIBrandPrimary};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.mono3};
    outline-color: transparent;
  }
`;

export const ellipsis = (lines: number = 2) => css`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

export const outlineButtonStates = css`
  ${baseState};
  border-color: ${props => props.theme.colors.mono14};
  color: ${props => props.theme.colors.mono14};

  svg {
    color: ${props => props.theme.colors.mono14};
  }

  &:hover {
    border-color: ${props => props.theme.colors.mono12};
    background-color: ${props => props.theme.colors.mono13}19;
    color: ${props => props.theme.colors.mono12};
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.colors.newUIBrandPrimary};
  }

  &:active {
    background-color: ${props => props.theme.colors.mono13}33;
    border-color: ${props => props.theme.colors.mono13};
    color: ${props => props.theme.colors.mono13};
    outline-color: ${props => props.theme.colors.transparent};
  }
`;

export const linkStates = css`
  ${baseState};
  border-radius: 0.125rem;
  color: ${({ theme }) => theme.colors.newUIBrandPrimary};
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.buttonNewPrimaryHover};
  }

  &:active {
    color: ${({ theme }) => theme.colors.buttonNewPrimaryActive};
  }

  &:focus-visible {
    color: ${({ theme }) => theme.colors.newUIBrandPrimary};
    outline: 0.125rem solid ${({ theme }) => theme.colors.newUIBrandPrimary};
  }
`;

export const listButtonStates = css`
  ${baseState};
  border: 0.125rem solid ${props => props.theme.colors.mono3};
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.colors.black};

  &[aria-selected='true'] {
    border-color: ${({ theme }) => theme.colors.black};
    background-color: ${({ theme }) => theme.colors.mono2};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    &:hover {
      border-color: ${({ theme }) => theme.colors.newUIBrandPrimary};
    }

    &:active {
      background-color: ${({ theme }) => theme.colors.gray5};
      border-color: ${({ theme }) => theme.colors.buttonNewPrimaryActive};
    }

    &:focus-visible {
      outline-color: ${({ theme }) => theme.colors.newUIBrandPrimary};
    }
  }
`;

export const dropdownStates = css`
  ${baseState};

  &:hover {
    background-color: ${({ theme }) => theme.colors.mono2};
    color: ${({ theme }) => theme.colors.newUIBrandPrimary};

    svg:last-child {
      color: ${({ theme }) => theme.colors.black};
    }

    svg:first-child {
      color: ${({ theme }) => theme.colors.newUIBrandPrimary};
    }
  }

  &:focus-visible {
    color: ${({ theme }) => theme.colors.black};
    outline: 0.125rem solid ${({ theme }) => theme.colors.newUIBrandPrimary};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.mono3};
    color: ${({ theme }) => theme.colors.newUIBrandPrimary};
    outline-color: ${props => props.theme.colors.transparent};

    svg {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

export const GlobalStyle = createGlobalStyle`
  body {
    position: relative;
  }
  /*
    Allow percentage-based heights in the application
  */
  html,
  body {
    height: 100%;
  }
`;
